import { useEffect, useRef, useState } from 'react';
import { ConfigProvider, DatePicker, Radio, Space, Tag } from 'antd';
import heIL from 'antd/locale/he_IL';
import dayjs from 'dayjs';

/**
 * Date-range filter — same look as the Arbox Tasks-page "תאריך מכירה" filter.
 *
 *   Trigger : antd <Tag> chip showing "PREFIX: DD/MM/YYYY - DD/MM/YYYY".
 *   Menu    : vertical Radio.Group with 7 presets, plus an always-visible
 *             RangePicker bound to the same value.
 *
 * Presets are day-level (deposits are filtered by issue/due date, not by tax
 * reporting month):
 *   today / yesterday / this week / this month / last month / last 30d / custom.
 *
 * Emits onChange(fromDate, toDate) where both are 'YYYY-MM-DD'.
 */

type Mode =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'thisMonth'
  | 'prevMonth'
  | 'last30'
  | 'custom';

interface Period {
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD
}

function computePresets(): Record<Exclude<Mode, 'custom'>, Period> {
  const today = dayjs();
  const yesterday = today.subtract(1, 'day');
  // Sunday-based week (Israeli convention).
  const dow = today.day();
  const sunday = today.subtract(dow, 'day');
  const saturday = sunday.add(6, 'day');
  const monthStart = today.startOf('month');
  const monthEnd = today.endOf('month');
  const prevMonthStart = today.subtract(1, 'month').startOf('month');
  const prevMonthEnd = today.subtract(1, 'month').endOf('month');
  const last30Start = today.subtract(29, 'day');
  const f = (d: dayjs.Dayjs) => d.format('YYYY-MM-DD');
  return {
    today: { from: f(today), to: f(today) },
    yesterday: { from: f(yesterday), to: f(yesterday) },
    thisWeek: { from: f(sunday), to: f(saturday) },
    thisMonth: { from: f(monthStart), to: f(monthEnd) },
    prevMonth: { from: f(prevMonthStart), to: f(prevMonthEnd) },
    last30: { from: f(last30Start), to: f(today) },
  };
}

export function getDefaultPeriod(): Period {
  return computePresets().thisMonth;
}

interface Props {
  value: { from?: string; to?: string };
  onChange: (from: string, to: string) => void;
  /** Prefix shown on the chip (default "תאריך"). Pass null to hide. */
  buttonPrefix?: string | null;
}

export default function ReportingPeriodFilter({
  value,
  onChange,
  buttonPrefix = 'תאריך',
}: Props) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('thisMonth');
  const ref = useRef<HTMLDivElement>(null);

  // Default: pick this month if nothing is set.
  useEffect(() => {
    if (!value.from) {
      const p = computePresets().thisMonth;
      onChange(p.from, p.to);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectPreset = (key: Mode) => {
    setMode(key);
    if (key === 'custom') return; // user picks via the RangePicker below
    const presets = computePresets();
    const p = presets[key];
    onChange(p.from, p.to);
    // Stay open — the RangePicker reflects the new range and the user can
    // tweak from there. The Tasks-page pattern keeps the menu open until the
    // user clicks outside.
  };

  // Chip label always shows the resolved range as DD/MM/YYYY - DD/MM/YYYY.
  const chipLabel = (() => {
    if (!value.from || !value.to) return 'בחר תאריך';
    const from = dayjs(value.from);
    const to = dayjs(value.to);
    return `${from.format('DD/MM/YYYY')} - ${to.format('DD/MM/YYYY')}`;
  })();

  return (
    <div className="relative" ref={ref}>
      <Tag
        onClick={() => setOpen(!open)}
        style={{
          backgroundColor: '#ffffff',
          cursor: 'pointer',
          color: '#3b3b3b',
          borderColor: '#ffffff',
          paddingBlock: 4.8,
          paddingInline: 9.6,
          fontSize: 16,
          display: 'flex',
          alignItems: 'center',
          columnGap: 5,
          height: 38,
          marginInlineEnd: 0,
        }}
      >
        {buttonPrefix ? `${buttonPrefix}: ${chipLabel}` : chipLabel}
        <span
          className="transition-transform"
          style={{
            fontSize: 10,
            lineHeight: 1,
            transform: open ? 'rotate(180deg)' : 'none',
          }}
          aria-hidden
        >
          ▾
        </span>
      </Tag>

      {open && (
        <div className="absolute z-50 top-full mt-2 bg-white rounded shadow-popup border border-border min-w-[280px] py-3 px-4">
          <ConfigProvider direction="rtl" locale={heIL}>
            <Radio.Group
              value={mode}
              onChange={(e) => selectPreset(e.target.value as Mode)}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Radio value="today">היום</Radio>
                <Radio value="yesterday">אתמול</Radio>
                <Radio value="thisWeek">השבוע</Radio>
                <Radio value="thisMonth">החודש</Radio>
                <Radio value="prevMonth">חודש שעבר</Radio>
                <Radio value="last30">30 הימים האחרונים</Radio>
                <Radio value="custom">טווח תאריכים</Radio>
              </Space>
            </Radio.Group>

            {/* Always-visible RangePicker — clicking a preset updates the
                values; switching to a custom range edits them directly. */}
            <div className="pt-3 mt-3 border-t border-border">
              <DatePicker.RangePicker
                size="small"
                placeholder={['תאריך התחלה', 'תאריך סיום']}
                format="DD/MM/YYYY"
                value={
                  value.from && value.to
                    ? [dayjs(value.from), dayjs(value.to)]
                    : null
                }
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setMode('custom');
                    onChange(
                      dates[0].format('YYYY-MM-DD'),
                      dates[1].format('YYYY-MM-DD'),
                    );
                  }
                }}
                getPopupContainer={(t) => t.parentElement || document.body}
                style={{ width: '100%' }}
              />
            </div>
          </ConfigProvider>
        </div>
      )}
    </div>
  );
}
